/**
 * DevToolsPanel — shared style tokens + small helpers.
 *
 * Pulls the panel chrome onto theme tokens (zero styled-components). Each
 * export is a plain `React.CSSProperties` object or a pure helper so it
 * spreads straight into inline styles. Behavior-preserving: the values map
 * 1:1 onto the pre-existing hardcoded palette via the semantic theme tokens,
 * so adoption is a visual no-op.
 */
import type { CSSProperties } from 'react';
import {
  COLORS,
  RAW,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  FONTS,
  SHADOW,
  Z,
  DURATION,
  EASING,
} from '../../theme';

/** Fixed-position draggable container. left/top/width/height are injected by the caller. */
export function containerStyle(args: {
  left: number;
  top: number;
  width: number;
  height: number;
  collapsed: boolean;
  animate: boolean;
}): CSSProperties {
  return {
    position: 'fixed',
    left: args.left,
    top: args.top,
    width: args.collapsed ? 40 : args.width,
    height: args.collapsed ? 40 : args.height,
    background: COLORS.overlayScrimStrong,
    border: `1px solid ${COLORS.borderSubtle}`,
    borderRadius: RADII.lg,
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.textPrimary,
    fontFamily: FONTS.ui,
    pointerEvents: 'auto',
    zIndex: Z.debug,
    transition: args.animate
      ? `width ${DURATION.slow} ${EASING.standard}, height ${DURATION.slow} ${EASING.standard}`
      : 'none',
    overflow: 'hidden',
    boxShadow: SHADOW.lg,
  };
}

export const headerBarStyle: CSSProperties = {
  display: 'flex',
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
  background: COLORS.overlayScrimLight,
};

export const tabContainerStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  cursor: 'grab',
};

export const resizeHandleStyle: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  right: 0,
  width: 15,
  height: 15,
  cursor: 'nwse-resize',
  background: `linear-gradient(135deg, transparent 50%, ${COLORS.border} 50%)`,
  zIndex: 10,
};

export const contentStyle: CSSProperties = {
  padding: SPACE.xl,
  overflowY: 'auto',
  flex: 1,
};

/** Header icon-toggle button. Active colour + glow are injected per-toggle. */
export function toggleButtonStyle(args: {
  active: boolean;
  activeColor: string;
  glowRgba: string;
}): CSSProperties {
  return {
    padding: SPACE.lg,
    background: 'transparent',
    border: 'none',
    color: args.active ? args.activeColor : RAW.gray,
    cursor: 'pointer',
    fontSize: FONT_SIZE.lg,
    transition: `color ${DURATION.base} ${EASING.standard}`,
    filter: args.active ? `drop-shadow(0 0 5px ${args.glowRgba})` : 'none',
  };
}

export function collapseButtonStyle(collapsed: boolean): CSSProperties {
  return {
    padding: SPACE.lg,
    background: 'transparent',
    border: 'none',
    color: RAW.gray,
    cursor: 'pointer',
    width: collapsed ? '100%' : 'auto',
    borderLeft: collapsed ? 'none' : `1px solid ${COLORS.borderSubtle}`,
  };
}

export const whitelistBannerStyle: CSSProperties = {
  background: 'rgba(241, 196, 15, 0.2)',
  border: '1px solid rgba(241, 196, 15, 0.5)',
  borderRadius: RADII.md,
  padding: `${SPACE.md}px ${SPACE.lg}px`,
  marginBottom: SPACE.lg,
  fontSize: FONT_SIZE.sm,
  fontFamily: FONTS.mono,
  color: COLORS.warning,
};

export const whitelistBannerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.md,
};

export const whitelistBannerHintStyle: CSSProperties = {
  marginTop: SPACE.xs,
  opacity: 0.8,
  fontSize: FONT_SIZE.xs,
};

export const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: RAW.slate,
  textAlign: 'center',
  padding: SPACE.xxl,
};

export const emptyStateIconStyle: CSSProperties = {
  fontSize: 48,
  marginBottom: SPACE.xl,
};

export const emptyStateTitleStyle: CSSProperties = {
  fontSize: FONT_SIZE.xl,
  fontWeight: FONT_WEIGHT.bold,
  marginBottom: SPACE.md,
};

export const emptyStateLabelStyle: CSSProperties = {
  fontSize: FONT_SIZE.md,
  opacity: 0.7,
  marginBottom: SPACE.md,
};

export const emptyStateWhitelistStyle: CSSProperties = {
  fontSize: FONT_SIZE.sm,
  color: COLORS.info,
  fontFamily: FONTS.mono,
  background: 'rgba(52, 152, 219, 0.1)',
  padding: `${SPACE.md}px ${SPACE.lg}px`,
  borderRadius: RADII.md,
};

export const emptyStateHintStyle: CSSProperties = {
  fontSize: FONT_SIZE.md,
  opacity: 0.7,
};
